/**
 * Checks whether a data row (as an object with multiple keys) is empty.
 * A row is empty if each of its keys contain empty values.
 * @param row the row
 */
const rowEmpty = (row) => !row
    ? true
    : Object.keys(row).every(key => !row[key] || (Array.isArray(row[key]) && row[key].length === 0))

const arraysHaveSameElements = (arr1, arr2) => (arr1.length === arr2.length && arr1.every(col => arr2.includes(col)))

/**
 * Converts a given array to a map using two functions, one for the key, the other for the value.
 */
 const arrayToMap = (arr, keyFunc, valueFunc) => {
    const map = {}
    arr.forEach(elem => {
        map[keyFunc(elem)] = valueFunc(elem)
    })
    return map
}

const overlapping = (arr1, arr2) => arr1.some(e => arr2.includes(e))
    ? true
    : arr2.some(e => arr1.includes(e))

/**
 * Check whether two rows should be merged, which is the case if they have the same columns with non-overlapping values.
 *
 * @param row1 the first row
 * @param row2 the second row
 */
const shouldMergeRows = (row1, row2) => {
    if (!arraysHaveSameElements(Object.keys(row1), Object.keys(row2))) {
        return false
    }

    const prevRowColsWithNonEmptyValues = Object.keys(row1).filter(col => !!row1[col] && row1[col].length > 0)
    const currentRowColsWithNonEmptyValues = Object.keys(row2).filter(col => !!row2[col] && row2[col].length > 0)

    return !overlapping(prevRowColsWithNonEmptyValues, currentRowColsWithNonEmptyValues)
}

/**
 * Merges the values of non-empty columns of the second row into the first row, returns the resulting row as a copy (does not modify the original rows).
 *
 * @param row1 the first row
 * @param row2 the second row
 */
const mergeRows = (row1, row2) => {
    const result = {}

    Object.keys(row1).forEach(c => result[c] = row1[c])
    Object.keys(row2).filter(c => !!row2[c] && row2[c].length > 0).forEach(c => result[c] = row2[c])

    return result
}

/**
 * Waits the specified number of milliseconds before resolving the promise. Used for throttling operations.
 * @param millis the number of millis to wait
 */
const delay = async (millis) => new Promise( (resolve) => {
    setTimeout( () => resolve(), millis)
})

/**
 * Sanitizes the given string to be used as a filename. If it's empty, "unnamed" is returned.
 * @param str the input string
 */
const sanitizeStringForFilename = (str) => !str ? 'unnamed' : str.replaceAll(/\s+/g, "_").replaceAll(/\W/g, "")

/**
 * Extracts the ID of a Google Sheet from its URL.
 * @param {*} url the url
 */
 const extractSpreadSheetId = (url) => {
    if (!url)  return null

    const captured = url.match(/\/d\/(.+)\//)

    return !captured || captured.length < 2 ? null : captured[1]
}

/**
 * Returns a Google Sheet's URL based on its ID.
 * @param {*} id the ID
 */
const getSpreadsheetUrl = (id) => !id ? '' : `https://docs.google.com/spreadsheets/d/${id}/edit`


module.exports = {rowEmpty, arrayToMap, shouldMergeRows, mergeRows, delay, sanitizeStringForFilename, 
    extractSpreadSheetId, getSpreadsheetUrl}